import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getPaymentMethodOptions } from 'src/shared/models/enums/paymentMethod.enum';
import { updateFeePeriod } from 'src/shared/services/feePeriodService';
import { AssociationFeeTable } from './associationFeeTable';
import { AssociationFeeRequest } from '../models/associationFeeRequest';
import { FeeLevelEnum, FeePeriod } from '../models/feePeriod';

type FeePeriodEditorProps = {
  level: FeeLevelEnum;
  period: FeePeriod;
  autogiroFee?: boolean;
  swishFee?: boolean;
  amountHeader: string;
  showFriendFee?: boolean;
  distributedFee?: boolean;
  lockedProfiles?: boolean;
  onSaved: () => void;
};

/**
 * Beloppen för en avgift som ännu inte börjat gälla. Samma tabell som den löpande avgiften,
 * men med ett eget formulär, eftersom sidans formulär hör till den avgift som gäller nu.
 *
 * Aviseringsmetoden följer med från den avgift perioden skapades ur och visas bara. Att byta
 * metod mitt i en tidslinje är en egen fråga, beloppen har helt olika innebörd före och efter
 * ett sådant byte.
 */
export const FeePeriodEditor = (props: FeePeriodEditorProps) => {
  const { level, period, autogiroFee, swishFee, amountHeader, showFriendFee, distributedFee, lockedProfiles, onSaved } = props;
  const { t } = useTranslation();

  const [openAddprofile, setOpenAddprofile] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const paymentMethodOptions = getPaymentMethodOptions(t);
  const methodLabel = paymentMethodOptions.find((opt) => opt.value === period.fee.paymentMethod)?.description ?? '';

  const formMethods = useForm<AssociationFeeRequest>({
    mode: 'all',
    defaultValues: {
      paymentMethod: period.fee.paymentMethod,
      sameFeeForAll: period.fee.sameFeeForAll,
      sameFeeForAllAutogiro: period.fee.sameFeeForAllAutogiro ?? undefined,
      sameFeeForAllSwish: period.fee.sameFeeForAllSwish ?? undefined,
      mainAndFamily: period.fee.mainAndFamily ?? {
        feeMainMember: 0,
        feeFamilyMember: 0,
        feeMainMemberAutogiro: 0,
        feeFamilyMemberAutogiro: 0,
        feeMainMemberSwish: 0,
        feeFamilyMemberSwish: 0,
      },
      familySteps: period.fee.familySteps ?? [
        { member: 'mainMember', fee: 0 },
        { member: 'familyMember1', fee: 0 },
      ],
      oneFeeForHousehold: period.fee.oneFeeForHousehold,
      oneFeeForHouseholdAutogiro: period.fee.oneFeeForHouseholdAutogiro ?? undefined,
      oneFeeForHouseholdSwish: period.fee.oneFeeForHouseholdSwish ?? undefined,
      feeBasedOnProfile: period.fee.feeBasedOnProfile,
      feeFriend: period.fee.feeFriend ?? undefined,
      feeFriendAutogiro: period.fee.feeFriendAutogiro ?? undefined,
      feeFriendSwish: period.fee.feeFriendSwish ?? undefined,
    },
  });

  const onSave = async () => {
    setSaving(true);

    try {
      await updateFeePeriod(level, period.id, { ...formMethods.getValues(), validFrom: period.validFrom });

      toast.success(t('association:feePeriodSaved') as string);
      onSaved();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      toast.error((e?.data?.messages ?? e?.data ?? t('association:feePeriodSaveFailed')) as string);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
        {t('association:feePeriodMethod', { method: methodLabel })}
      </Typography>

      <AssociationFeeTable
        autogiroFee={autogiroFee}
        swishFee={swishFee}
        readOnlyMode={false}
        paymentMethod={period.fee.paymentMethod ?? 0}
        showFriendFee={showFriendFee}
        distributedFee={distributedFee}
        lockedProfiles={lockedProfiles}
        amountHeader={amountHeader}
        openAddprofile={openAddprofile}
        setOpenAddprofile={setOpenAddprofile}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {!lockedProfiles && (
          <Button variant='outlined' size='small' disableElevation onClick={() => setOpenAddprofile(true)}>
            <Add fontSize='small' />
            {t('association:addProfile')}
          </Button>
        )}

        <Button sx={{ ml: 2 }} variant='contained' size='small' disableElevation disabled={saving} onClick={onSave}>
          {t('common:button.save')}
        </Button>
      </Box>
    </FormProvider>
  );
};
